import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ComponentProps,
  type ReactNode,
  type Ref,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Input } from '@/components/ui/Input';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { cn } from '@/lib/utils';

export const CHANNEL_SURFACE_CLASS_NAME =
  "relative isolate overflow-hidden border border-[var(--glass-edge-strong)] bg-surface shadow-[inset_0_1px_0_var(--glass-highlight),0_0.5rem_1.5rem_var(--glass-shadow)] backdrop-blur-2xl backdrop-saturate-150 before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-[linear-gradient(135deg,var(--glass-highlight),transparent_38%,var(--glass-edge-subtle))] before:content-['']";

export const SOFT_INSET_CLASS_NAME =
  'border border-[var(--glass-edge)] bg-surface-hover backdrop-blur-xl';

type HomeCardRootProps = ComponentProps<typeof YStack>;

const HomeCardRoot = ({ className, ...props }: HomeCardRootProps) => (
  <YStack
    className={cn(
      CHANNEL_SURFACE_CLASS_NAME,
      'cursor-pointer rounded-[2rem] p-5',
      className
    )}
    {...props}
  />
);

type HomeCardIconProps = ComponentProps<typeof XStack>;

const HomeCardIcon = ({ className, ...props }: HomeCardIconProps) => (
  <XStack
    center
    aria-hidden
    className={cn(
      'size-10 shrink-0 rounded-full border border-[var(--glass-edge)] bg-surface-hover backdrop-blur-xl',
      className
    )}
    {...props}
  />
);

type HomeCardContentProps = ComponentProps<typeof YStack>;

const HomeCardContent = ({ className, ...props }: HomeCardContentProps) => (
  <YStack className={cn('gap-1.5', className)} {...props} />
);

type HomeCardTitleProps = ComponentProps<'h2'>;

const HomeCardTitle = ({ className, ...props }: HomeCardTitleProps) => (
  <h2 className={cn('text-xl font-medium', className)} {...props} />
);

type HomeCardDescriptionProps = ComponentProps<'p'>;

const HomeCardDescription = ({
  className,
  ...props
}: HomeCardDescriptionProps) => (
  <p
    className={cn('text-sm leading-snug text-foreground-subtle', className)}
    {...props}
  />
);

export const HomeCard = {
  Root: HomeCardRoot,
  Icon: HomeCardIcon,
  Content: HomeCardContent,
  Title: HomeCardTitle,
  Description: HomeCardDescription,
};

export const ChannelSurface = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <YStack
    className={cn('rounded-[2rem]', CHANNEL_SURFACE_CLASS_NAME, className)}
  >
    {children}
  </YStack>
);

export const ChannelBadge = ({
  channel,
  size = 'default',
}: {
  channel: {
    shortName: string;
    accentClassName: string;
    logoUrl?: string;
    logoBackgroundClassName?: string;
    logoClassName?: string;
  };
  size?: 'default' | 'large';
}) => (
  <XStack
    center
    className={cn(
      'shrink-0 overflow-hidden rounded-xl font-medium shadow-sm',
      channel.logoUrl
        ? (channel.logoBackgroundClassName ?? 'bg-transparent')
        : channel.accentClassName,
      size === 'large' ? 'size-14 text-base' : 'size-11 text-sm'
    )}
    aria-hidden
  >
    {channel.logoUrl ? (
      <img
        src={channel.logoUrl}
        alt=""
        className={cn('size-full object-cover', channel.logoClassName)}
      />
    ) : (
      channel.shortName
    )}
  </XStack>
);

export const ChannelPageHeader = ({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) => (
  <YStack className="gap-3">
    {eyebrow && (
      <span className="text-xs uppercase tracking-[0.16em] text-foreground-muted">
        {eyebrow}
      </span>
    )}
    <YStack className="gap-2">
      <h1 className="text-3xl font-light leading-tight tracking-[-0.03em] text-foreground sm:text-4xl">
        {title}
      </h1>
      {description && (
        <p className="max-w-xl text-base leading-relaxed text-foreground-muted">
          {description}
        </p>
      )}
    </YStack>
  </YStack>
);

export const ChannelSheetFooterSlot = createContext<HTMLDivElement | null>(
  null
);

export const ChannelRouteSheet = ({
  name,
  ariaLabel,
  scrollRef,
  children,
}: {
  name: string;
  ariaLabel: string;
  scrollRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
  const { openSheet, setOpenSheet } = BottomSheet.useBottomSheet();
  const wasOpened = useRef(false);

  useEffect(() => {
    setOpenSheet(name);
    return () => setOpenSheet(null);
  }, [name, setOpenSheet]);

  useEffect(() => {
    if (openSheet === name) {
      wasOpened.current = true;
      return;
    }
    if (wasOpened.current && openSheet === null) {
      void navigate({ to: '/channels', replace: true });
    }
  }, [name, navigate, openSheet]);

  return (
    <BottomSheet.Sheet
      name={name}
      ariaLabel={ariaLabel}
      className="mx-auto h-[92dvh] max-h-[92dvh] max-w-4xl overflow-hidden rounded-t-[2rem] border-[var(--glass-edge-strong)] bg-background-subtle [&_[data-slot=drawer-drag-handle]]:bg-foreground-muted"
    >
      <ScrollMask
        ref={scrollRef}
        showEndFade
        fadeSize={24}
        className="channel-sheet-scroll min-h-0 flex-1 px-5 pb-8 pt-2 sm:px-10 sm:pb-12"
      >
        <ChannelSheetFooterSlot.Provider value={footerSlot}>
          {children}
        </ChannelSheetFooterSlot.Provider>
      </ScrollMask>
      <Box ref={setFooterSlot} className="shrink-0" />
    </BottomSheet.Sheet>
  );
};

const BACK_CONTROL_CLASS_NAME = cn(
  'flex size-11 items-center justify-center rounded-full text-foreground-subtle hover:bg-surface-active hover:text-foreground focus-ring',
  SOFT_INSET_CLASS_NAME
);

export const DashboardBackLink = () => (
  <Link
    to="/channels"
    aria-label="Back to dashboard"
    title="Back to dashboard"
    className={BACK_CONTROL_CLASS_NAME}
  >
    <Icons.arrowLeft className="size-5" />
  </Link>
);

export const SheetBackButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={BACK_CONTROL_CLASS_NAME}
  >
    <Icons.arrowLeft className="size-5" />
  </button>
);

export const ChannelSearchInput = ({
  value,
  label,
  placeholder,
  onChange,
}: {
  value: string;
  label: string;
  placeholder: string;
  onChange: (value: string) => void;
}) => (
  <Box className="relative">
    <Icons.search className="pointer-events-none absolute left-4 top-1/2 z-10 size-5 -translate-y-1/2 text-foreground-muted" />
    <Input
      type="search"
      value={value}
      inputSize="lg"
      aria-label={label}
      placeholder={placeholder}
      className={cn('rounded-full pl-12 pr-5', SOFT_INSET_CLASS_NAME)}
      onChange={(event) => onChange(event.target.value)}
    />
  </Box>
);
